import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Gauge, Target, BookOpen, Radio, MapPin } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import FAQAccordion from "@/components/FAQAccordion";
import JsonLd from "@/components/JsonLd";
import useSEO from "@/lib/useSEO";
import {
  softwareApplicationSchema,
  faqPageSchema,
  breadcrumbSchema,
  SITE_URL,
} from "@/lib/schema";
import { CITIES, STENO_FAQ, STENO_KEYWORDS, BLOG_POSTS } from "@/data/content";

const STENO_URL = "https://stenopacemaster.com";

export default function Stenospace() {
  useSEO({
    title:
      "StenoPaceMaster — Online Stenography Speed Building Program | LexWorkspace",
    description:
      "The structured online stenography training program court reporters, CART providers and broadcast captioners use to build speed from 60 wpm to 280+ wpm. RPR / RMR / CRR / state CSR certification practice. Serving DC, NYC, LA, Chicago and Houston.",
    keywords:
      "stenography training online, stenography speed building program, court reporting speed practice, steno speed building classes, online steno practice drills, stenography practice for RPR exam, realtime stenography practice, stenography training for captioners",
    url: SITE_URL + "/stenopacemaster",
    image:
      "https://images.unsplash.com/photo-1558009250-d3d2229fdf28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzdGVub2dyYXBoeSUyMG1hY2hpbmUlMjBjb3VydCUyMHJlcG9ydGVyfGVufDB8fHx8MTc4MDcxODg2N3ww&ixlib=rb-4.1.0&q=85",
  });
  const stenoPosts = BLOG_POSTS.filter((p) => p.product === "steno");

  return (
    <div data-testid="stenospace-page">
      <JsonLd
        id="steno-software"
        data={softwareApplicationSchema({
          name: "StenoPaceMaster",
          url: STENO_URL,
          description:
            "Online stenography speed-building and court-reporting practice platform with graduated dictation, certification tracks (RPR, RMR, CRR, state CSRs) and realtime drills for CART providers and broadcast captioners.",
          category: "EducationalApplication",
          audience: "Court Reporters, CART Providers, Captioners, Steno Students",
        })}
      />
      <JsonLd id="steno-faq" data={faqPageSchema(STENO_FAQ)} />
      <JsonLd
        id="steno-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Stenography", url: SITE_URL + "/stenopacemaster" },
        ])}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1558009250-d3d2229fdf28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzdGVub2dyYXBoeSUyMG1hY2hpbmUlMjBjb3VydCUyMHJlcG9ydGVyfGVufDB8fHx8MTc4MDcxODg2N3ww&ixlib=rb-4.1.0&q=85"
            alt="Stenography machine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 lg:py-44 text-white">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs tracking-[0.3em] uppercase text-white/70 mb-6"
          >
            StenoPaceMaster · Online stenography practice platform
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="steno-h1"
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl"
          >
            Build real steno speed.{" "}
            <span className="text-[#93C5FD]">Online, every day.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl"
          >
            The structured online stenography training program court reporters,
            CART providers and broadcast captioners use to take their speed
            from 60 wpm to 280+ wpm — with graded dictation, certification
            tracks for RPR / RMR / CRR / state CSRs, and daily accuracy drills.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <a
              href={STENO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="steno-hero-cta-primary"
              className="btn-sharp bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
            >
              Visit stenopacemaster.com <ArrowUpRight size={14} />
            </a>
            <a
              href="#program"
              data-testid="steno-hero-cta-secondary"
              className="btn-sharp border border-white text-white hover:bg-white hover:text-black"
            >
              See the program
            </a>
          </motion.div>
        </div>
      </section>

      {/* PROGRAM PILLARS */}
      <section id="program" className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            What&apos;s inside the program
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none max-w-3xl mb-16">
            Four practice modes that actually move the needle.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-black/10">
            {[
              {
                icon: Gauge,
                title: "Speed Building",
                body: "Graduated dictation from 60 wpm to 280+ wpm. Literary, Jury Charge, Q&A.",
              },
              {
                icon: Target,
                title: "Accuracy Drills",
                body: "Per-stroke error reports. Build a private clean-up list and beat your weak words.",
              },
              {
                icon: BookOpen,
                title: "Certification Tracks",
                body: "RPR, RMR, CRR, CART, plus all state CSR formats with timed take-and-grade.",
              },
              {
                icon: Radio,
                title: "Realtime & Captioning",
                body: "Broadcast dictation, sports rosters, proper nouns, untranslate tracker.",
              },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`steno-pillar-${i}`}
                  className={
                    "p-8 lg:p-10 border-black/10 " +
                    (i < 3 ? "md:border-r border-b md:border-b-0" : "")
                  }
                >
                  <Icon
                    size={28}
                    className="text-[#1E3A8A] mb-6"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 lg:py-32 bg-[#EFF6FF] border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-semibold mb-4">
              Who it&apos;s for
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Working professionals and serious students.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              {
                t: "Court reporters",
                b: "Push past plateaus, prep for higher-tier certifications, recover speed after time off.",
              },
              {
                t: "Steno students",
                b: "Move efficiently through 60-225 wpm with a daily structure that doesn't burn you out.",
              },
              {
                t: "CART providers",
                b: "Realtime accuracy, dictionary maintenance, university-level live captioning prep.",
              },
              {
                t: "Broadcast captioners",
                b: "Fast-news dictation, sports rosters, proper nouns and 240+ wpm interview drills.",
              },
              {
                t: "Legal transcribers",
                b: "Audio-to-text fluency for depositions and court audio.",
              },
              {
                t: "Re-entry pros",
                b: "Returning after a break? Diagnostic test in week one, structured ramp from there.",
              },
            ].map((it, i) => (
              <div key={i} data-testid={`steno-audience-${i}`} className="border-t border-black/10 pt-6">
                <p className="font-serif-display text-2xl mb-2">{it.t}</p>
                <p className="text-sm text-gray-700 leading-relaxed">{it.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITY LINKS */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
                Stenography by city
              </p>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
                Local context. National program.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-black/10">
            {CITIES.map((c, i) => (
              <Link
                key={c.slug}
                to={`/steno/${c.slug}`}
                data-testid={`steno-city-${c.slug}`}
                className={
                  "p-6 lg:p-8 hover:bg-[#EFF6FF] transition-colors group " +
                  (i < CITIES.length - 1 ? "border-r border-black/10" : "") +
                  (i < 2 ? " border-b lg:border-b-0 border-black/10" : "")
                }
              >
                <MapPin size={18} className="text-[#1E3A8A] mb-4" />
                <p className="font-serif-display text-2xl mb-2 leading-tight">
                  {c.short}
                </p>
                <p className="text-xs text-gray-500">
                  {c.stenoStats.reporters} reporters
                </p>
                <p className="text-xs tracking-widest uppercase font-semibold text-[#1E3A8A] mt-4 group-hover:underline">
                  Open →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-none mb-12">
            Steno reading.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stenoPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`steno-blog-${p.slug}`}
                className="group border border-black/10 bg-white hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-2xl mb-3 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {p.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Stenography FAQ
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Common questions.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={STENO_FAQ} testIdPrefix="steno-faq" />
          </div>
        </div>
      </section>

      {/* CTA + LEAD */}
      <section className="bg-[#1E3A8A] text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-4">
              Ready to start?
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-none">
              Subscribe to the practice platform.
            </h2>
            <p className="text-white/70 mt-6 max-w-md leading-relaxed">
              Get the structured program, certification tracks, and daily
              dictation library on stenopacemaster.com — or join our list to
              receive a free 7-day speed-building plan first.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={STENO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="steno-bottom-cta"
                className="btn-sharp bg-white text-[#1E3A8A] hover:bg-white/90 inline-flex items-center gap-2"
              >
                Open stenopacemaster.com <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div>
            <LeadForm
              interest="steno"
              source="stenospace-page-cta"
              variant="dark"
              outboundUrl={STENO_URL}
              testIdPrefix="steno-lead"
              ctaLabel="Send My Free Plan"
            />
          </div>
        </div>
      </section>

      {/* HIDDEN KEYWORD CLOUD FOR SEO (visible but tertiary) */}
      <section className="py-16 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
            Topics we cover
          </p>
          <div className="flex flex-wrap gap-2">
            {STENO_KEYWORDS.map((k) => (
              <span
                key={k}
                className="text-xs px-3 py-1.5 border border-black/15 text-gray-700"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
